import React, { useEffect, useRef, useState, memo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import EarlyAccessModal from '../components/EarlyAccessModal'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, BarChart, Bar, ReferenceLine,
} from 'recharts'
import {
  Bot, TrendingUp, TrendingDown, Zap, Shield, Globe, BookOpen,
  ArrowRight, Activity, CheckCircle, Bell, BarChart2, Users,
  ChevronRight, Lock, RefreshCw, Award, DollarSign, Cpu, Target,
  Play, Pause, Star, Menu, X, Sparkles, Signal, ShoppingBag,
  GraduationCap, Download, Headphones, Volume2, VolumeX, Film,
} from 'lucide-react'

// PDF.js worker (Vite resolves import.meta.url for bundling)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

// ─── Scroll-reveal hook ──────────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } },
      { threshold, rootMargin: '-40px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

// ─── Animated counter ────────────────────────────────────────────────────────
function Counter({ target, prefix = '', suffix = '', decimals = 0 }) {
  const [n, setN] = useState(0)
  const [ref, visible] = useReveal(0.5)
  useEffect(() => {
    if (!visible) return
    const dur = 1800, start = Date.now()
    const t = setInterval(() => {
      const p = Math.min((Date.now() - start) / dur, 1)
      const e = 1 - Math.pow(1 - p, 3)
      setN(+(target * e).toFixed(decimals))
      if (p >= 1) clearInterval(t)
    }, 16)
    return () => clearInterval(t)
  }, [visible, target, decimals])
  return <span ref={ref}>{prefix}{decimals ? n.toFixed(decimals) : n.toLocaleString()}{suffix}</span>
}

// ─── Fade-in wrapper ─────────────────────────────────────────────────────────
function Reveal({ children, className = '', delay = 0, direction = 'up' }) {
  const [ref, visible] = useReveal()
  const from = direction === 'up' ? 'translateY(35px)' : direction === 'left' ? 'translateX(-35px)' : direction === 'right' ? 'translateX(35px)' : 'translateY(0)'
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0)' : from,
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}

// ─── Mock data ───────────────────────────────────────────────────────────────
function genPortfolio(n = 30) {
  let v = 100000
  return Array.from({ length: n }, (_, i) => {
    v += (Math.random() - 0.38) * 2200
    if (v < 88000) v = 92000
    const d = new Date(2026, 3, i + 1)
    return { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), value: Math.round(v) }
  })
}
function genPrice(n = 60) {
  let p = 43500
  return Array.from({ length: n }, (_, i) => {
    p += (Math.random() - 0.46) * 320; if (p < 38000) p = 39000
    return { i, price: Math.round(p) }
  })
}

const PORTFOLIO_STATIC = genPortfolio()
const EXCHANGES = [
  { name: 'BINANCE', color: '#F0B90B' },
  { name: 'BYBIT', color: '#F7A600' },
  { name: 'Coinbase Advanced', color: '#0052FF' },
  { name: 'OKX', color: '#ffffff' },
  { name: 'KUCOIN', color: '#26A17B' },
  { name: 'KRAKEN', color: '#9146FF' },
  { name: 'BITGET', color: '#00CED1' },
  { name: 'GATE.IO', color: '#2354E6' },
]

const FEATURES = [
  { icon: Bot, title: 'AI Trading Bots', desc: 'Create and run intelligent trading bots 24/7 without writing a single line of code.', color: '#7c3aed' },
  { icon: BarChart2, title: 'Backtesting Engine', desc: 'Test strategies on real historical data with professional analytics and detailed reports.', color: '#2563eb' },
  { icon: Signal, title: 'Signal Marketplace', desc: 'Buy, sell, and monetize high-quality trading signals and strategies from proven traders.', color: '#059669' },
  { icon: Shield, title: 'Risk Management', desc: 'Advanced risk controls to protect your capital in any market condition automatically.', color: '#ea580c' },
  { icon: Globe, title: 'Multi-Exchange', desc: 'Connect multiple exchanges securely via API with full CCXT integration support.', color: '#0891b2' },
  { icon: BookOpen, title: 'Trading Academy', desc: 'Learn algorithmic trading from beginner to advanced with expert-led courses.', color: '#9333ea' },
]

const BOT_PERFORMANCE = [
  { name: 'AI Trend Hunter', pnl: '+$1,250.75', pct: '+12.45%', pos: true },
  { name: 'Scalper Pro', pnl: '+$832.21', pct: '+8.32%', pos: true },
  { name: 'Grid Master', pnl: '+$719.25', pct: '+7.25%', pos: true },
  { name: 'DCA Inteligente', pnl: '+$321.45', pct: '+4.32%', pos: true },
  { name: 'Mean Reversion AI', pnl: '-$120.35', pct: '-1.25%', pos: false },
]

// Static equity curve for backtesting demo (deterministic)
const BACKTEST_EQUITY = (() => {
  const pts = [100,102,105,103,107,110,108,113,116,114,119,123,121,126,130,128,133,137,135,140,144,142,147,152,150,155,160,157,163,168,165,171,177,174,180,186,183,190,196,193,200,207,204,211,218,215,222,229,226,233,241]
  return pts.map((v, i) => ({ i, value: Math.round(v * 1000) }))
})()

const SIGNALS = [
  { name: 'Alpha Signals', pct: '+24.35%', winRate: '72.4%', trades: 156, followers: '2.3K', color: '#00d395', spark: [3,5,4,7,6,9,8,11,10,14,13,16] },
  { name: 'Crypto Whale', pct: '+18.67%', winRate: '68.9%', trades: 203, followers: '1.8K', color: '#a78bfa', spark: [2,4,3,5,4,7,6,8,7,10,9,12] },
  { name: 'Momentum Pro', pct: '+15.23%', winRate: '65.2%', trades: 89, followers: '1.1K', color: '#60a5fa', spark: [1,3,2,4,5,4,6,5,7,6,8,9] },
]

const COURSES = [
  { icon: TrendingUp, label: 'Algorithmic Trading Basics', level: 'Beginner', color: '#7c3aed' },
  { icon: BarChart2, label: 'Strategy Development', level: 'Intermediate', color: '#2563eb' },
  { icon: Activity, label: 'Backtesting Mastery', level: 'Intermediate', color: '#0891b2' },
  { icon: Shield, label: 'Risk Management', level: 'Advanced', color: '#ea580c' },
  { icon: Bot, label: 'Advanced Automation', level: 'Advanced', color: '#00d395' },
]

// ─── Live market data ─────────────────────────────────────────────────────────
const MARKET_COINS = [
  { sym: 'BTCUSDT',  label: 'BTC',  name: 'Bitcoin',    color: '#f7931a' },
  { sym: 'ETHUSDT',  label: 'ETH',  name: 'Ethereum',   color: '#627eea' },
  { sym: 'SOLUSDT',  label: 'SOL',  name: 'Solana',     color: '#9945ff' },
  { sym: 'BNBUSDT',  label: 'BNB',  name: 'BNB',        color: '#f0b90b' },
  { sym: 'XRPUSDT',  label: 'XRP',  name: 'XRP',        color: '#00aae4' },
  { sym: 'AVAXUSDT', label: 'AVAX', name: 'Avalanche',  color: '#e84142' },
  { sym: 'ADAUSDT',  label: 'ADA',  name: 'Cardano',    color: '#3cc8c8' },
  { sym: 'DOTUSDT',  label: 'DOT',  name: 'Polkadot',   color: '#e6007a' },
  { sym: 'LINKUSDT', label: 'LINK', name: 'Chainlink',  color: '#2a5ada' },
  { sym: 'DOGEUSDT', label: 'DOGE', name: 'Dogecoin',   color: '#c3a634' },
]

function fmtPrice(price) {
  if (price == null || isNaN(price)) return '...'
  if (price >= 1000)  return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (price >= 10)    return '$' + price.toFixed(2)
  if (price >= 1)     return '$' + price.toFixed(4)
  return '$' + price.toFixed(5)
}

function useLivePrices() {
  const [prices, setPrices] = useState({})

  useEffect(() => {
    // Initial snapshot via REST
    const syms = JSON.stringify(MARKET_COINS.map(c => c.sym))
    fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=${encodeURIComponent(syms)}`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return
        const init = {}
        data.forEach(t => {
          init[t.symbol] = {
            price:  parseFloat(t.lastPrice),
            change: parseFloat(t.priceChangePercent),
            high:   parseFloat(t.highPrice),
            low:    parseFloat(t.lowPrice),
          }
        })
        setPrices(init)
      })
      .catch(() => {})

    // WebSocket for real-time ticks
    const streams = MARKET_COINS.map(c => c.sym.toLowerCase() + '@ticker').join('/')
    const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`)
    ws.onmessage = e => {
      try {
        const { data: d } = JSON.parse(e.data)
        if (d?.s) {
          setPrices(prev => ({
            ...prev,
            [d.s]: { price: parseFloat(d.c), change: parseFloat(d.P), high: parseFloat(d.h), low: parseFloat(d.l) },
          }))
        }
      } catch {}
    }
    return () => { if (ws.readyState < 2) ws.close() }
  }, [])

  return prices
}

// ─── Particle Canvas ─────────────────────────────────────────────────────────
const ParticleCanvas = memo(function ParticleCanvas() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let id
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const N = 55
    const pts = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.5 + 0.8,
    }))
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(139,92,246,0.55)'; ctx.fill()
      })
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y
        const d = Math.sqrt(dx * dx + dy * dy)
        if (d < 110) {
          ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y)
          ctx.strokeStyle = `rgba(139,92,246,${0.18 * (1 - d / 110)})`
          ctx.lineWidth = 0.6; ctx.stroke()
        }
      }
      id = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(id); ro.disconnect() }
  }, [])
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-25 pointer-events-none" />
})

// ─── Custom Tooltip ──────────────────────────────────────────────────────────
function ChartTip({ active, payload, label, prefix = '$' }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'rgba(8,8,20,0.95)', border: '1px solid rgba(124,58,237,0.5)', borderRadius: 8, padding: '8px 12px', backdropFilter: 'blur(10px)' }}>
      {label && <p style={{ color: '#8b5cf6', fontSize: 11, marginBottom: 2 }}>{label}</p>}
      <p style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{prefix}{Number(payload[0]?.value ?? 0).toLocaleString()}</p>
    </div>
  )
}

// ─── Platform Video ───────────────────────────────────────────────────────────
function PlatformVideoSection() {
  const [lang, setLang] = useState('en')
  const [started, setStarted] = useState(false)
  const videoRef = useRef(null)

  const switchLang = useCallback((l) => {
    setLang(l)
    setStarted(false)
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0 }
  }, [])

  const handlePlay = () => {
    setStarted(true)
    if (videoRef.current) videoRef.current.play()
  }

  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#070714' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center text-center gap-3 mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>
              <Film size={12} /> PLATFORM DEMO
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
              See TradeBotHub Pro in Action
            </h2>
            <p className="text-gray-400 text-sm max-w-xl">
              Watch how our AI agents trade autonomously, manage risk, and compound returns 24/7 — no coding required.
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="relative rounded-2xl overflow-hidden"
            style={{ border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 0 80px rgba(124,58,237,0.18)' }}>

            {/* Language toggle */}
            <div className="absolute top-4 right-4 z-30 flex rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(12px)' }}>
              {[['en', '🇺🇸 EN'], ['es', '🇪🇸 ES']].map(([l, label]) => (
                <button key={l} onClick={() => switchLang(l)}
                  className="px-3.5 py-1.5 text-xs font-bold transition-all duration-200"
                  style={{
                    background: lang === l ? 'rgba(124,58,237,0.85)' : 'rgba(0,0,0,0.55)',
                    color: lang === l ? '#fff' : '#9ca3af',
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Thumbnail + play button overlay */}
            {!started && (
              <div
                className="absolute inset-0 z-20 cursor-pointer group"
                onClick={handlePlay}
              >
                <img
                  src="/media/video-thumb.png"
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                  style={{ display: 'block' }}
                />
                {/* Dark gradient scrim */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.2) 100%)' }} />
                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="flex items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-110"
                    style={{
                      width: 80, height: 80,
                      background: 'rgba(124,58,237,0.9)',
                      boxShadow: '0 0 0 16px rgba(124,58,237,0.18), 0 0 60px rgba(124,58,237,0.45)',
                    }}
                  >
                    <Play size={32} className="text-white" style={{ marginLeft: 4 }} />
                  </div>
                </div>
                {/* Bottom label */}
                <div className="absolute bottom-0 left-0 right-0 px-5 py-4">
                  <p className="text-white text-sm font-semibold drop-shadow">
                    {lang === 'en' ? 'TradeBotHub Pro — Full Platform Walkthrough' : 'TradeBotHub Pro — Recorrido Completo de la Plataforma'}
                  </p>
                </div>
              </div>
            )}

            <video
              key={lang}
              ref={videoRef}
              className="w-full block"
              controls
              preload="none"
              poster="/media/video-thumb.png"
              style={{ background: '#000', maxHeight: '70vh' }}
            >
              <source src={`/media/video-${lang}.mp4`} type="video/mp4" />
            </video>

            {started && (
              <div className="flex items-center justify-between px-4 py-2.5"
                style={{ background: 'rgba(0,0,0,0.7)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-gray-400 text-[10px] font-semibold tracking-wide">
                  {lang === 'en' ? 'TradeBotHub Pro — Full Platform Walkthrough' : 'TradeBotHub Pro — Recorrido Completo de la Plataforma'}
                </span>
                <span className="text-[10px] text-violet-400 font-semibold">HD</span>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── Audio Guide ──────────────────────────────────────────────────────────────
function AudioSection() {
  const [lang, setLang]       = useState('en')
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted]     = useState(false)
  const audioRef = useRef(null)

  const togglePlay = () => {
    if (!audioRef.current) return
    playing ? audioRef.current.pause() : audioRef.current.play()
  }
  const handleTime = () => {
    if (!audioRef.current) return
    setProgress(audioRef.current.currentTime)
    setDuration(audioRef.current.duration || 0)
  }
  const seek = (e) => {
    if (!audioRef.current || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    audioRef.current.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration
  }
  const fmt = (s) => isNaN(s) ? '0:00' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  const pct = duration ? (progress / duration) * 100 : 0

  const TRACKS = {
    en: { title: 'Build AI Trading Agents Without Code', sub: 'Full platform explainer · English' },
    es: { title: 'Agentes de IA que Operan por Ti', sub: 'Explicación completa · Español' },
  }
  const BAR_HEIGHTS = [30,55,45,70,40,85,60,45,80,35,65,50,90,40,70,55,45,80,35,65,50,75,40,60,85,45,55,70,35,80,50,65,40,75,55,45,85,60,35,70]

  return (
    <section className="py-14 sm:py-16" style={{ background: '#050510' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-2xl p-6 sm:p-8"
            style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 40px rgba(124,58,237,0.08)' }}>

            {/* Header */}
            <div className="flex items-center justify-between mb-6 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#2563eb)', boxShadow: '0 0 20px rgba(124,58,237,0.4)' }}>
                  <Headphones size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold truncate">{TRACKS[lang].title}</p>
                  <p className="text-gray-500 text-[10px] mt-0.5">{TRACKS[lang].sub}</p>
                </div>
              </div>
              <div className="flex rounded-lg overflow-hidden shrink-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                {[['en','🇺🇸'],['es','🇪🇸']].map(([l,f]) => (
                  <button key={l} onClick={() => { setLang(l); setPlaying(false); setProgress(0); setDuration(0) }}
                    className="px-2.5 py-1.5 text-xs font-bold transition-all"
                    style={{ background: lang===l ? 'rgba(124,58,237,0.7)' : 'transparent', color: lang===l ? '#fff' : '#6b7280' }}>
                    {f} {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Waveform bars */}
            <div className="flex items-center justify-center gap-[3px] h-10 mb-5">
              {BAR_HEIGHTS.map((h, i) => (
                <div key={i} className="rounded-full"
                  style={{
                    width: 3, height: `${h}%`,
                    background: (i / BAR_HEIGHTS.length) * 100 <= pct
                      ? 'linear-gradient(to top,#7c3aed,#a78bfa)'
                      : 'rgba(255,255,255,0.08)',
                    transition: 'background 0.15s',
                  }} />
              ))}
            </div>

            {/* Seek bar */}
            <div className="cursor-pointer mb-1.5" onClick={seek}>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#7c3aed,#2563eb)', transition: 'width 0.1s linear' }} />
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-gray-600 mb-5 tabular-nums">
              <span>{fmt(progress)}</span><span>{fmt(duration)}</span>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => { const m = !muted; setMuted(m); if (audioRef.current) audioRef.current.muted = m }}
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:opacity-80 transition-all"
                style={{ background: 'rgba(255,255,255,0.06)' }}>
                {muted ? <VolumeX size={15} className="text-gray-400" /> : <Volume2 size={15} className="text-gray-400" />}
              </button>
              <button onClick={togglePlay}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#2563eb)', boxShadow: playing ? '0 0 28px rgba(124,58,237,0.7)' : '0 0 16px rgba(124,58,237,0.3)' }}>
                {playing
                  ? <Pause size={22} fill="white" className="text-white" />
                  : <Play  size={22} fill="white" className="text-white" style={{ marginLeft: 2 }} />
                }
              </button>
              <a href={`/media/audio-${lang}.m4a`} download
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:opacity-80 transition-all"
                style={{ background: 'rgba(255,255,255,0.06)' }}>
                <Download size={15} className="text-gray-400" />
              </a>
            </div>

            <audio key={lang} ref={audioRef} preload="metadata"
              onTimeUpdate={handleTime} onLoadedMetadata={handleTime}
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              onEnded={() => { setPlaying(false); setProgress(0) }}>
              <source src={`/media/audio-${lang}.m4a`} type="audio/mp4" />
            </audio>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── PDF Brochure ─────────────────────────────────────────────────────────────
function BrochureSection() {
  const [lang, setLang]           = useState('en')
  const [numPages, setNumPages]   = useState(null)
  const [page, setPage]           = useState(1)
  const [pageWidth, setPageWidth] = useState(700)
  const [animKey, setAnimKey]     = useState(0)
  const containerRef = useRef(null)

  useEffect(() => {
    const update = () => {
      if (containerRef.current)
        setPageWidth(Math.min(containerRef.current.offsetWidth - 40, 780))
    }
    update()
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  const goTo = (n) => {
    if (!numPages || n < 1 || n > numPages) return
    setAnimKey(k => k + 1)
    setPage(n)
  }

  const pdfSrc = lang === 'en' ? '/media/brochure-en.pdf' : '/media/brochure-es.pdf'

  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#070714' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center text-center gap-3 mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)', color: '#38bdf8' }}>
              <BookOpen size={12} /> PLATFORM OVERVIEW
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
              Full Platform Documentation
            </h2>
            <p className="text-gray-400 text-sm max-w-xl">
              Explore our complete platform guide — architecture, AI features, and roadmap. Available in English and Spanish.
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="rounded-2xl overflow-hidden"
            style={{ border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 60px rgba(14,165,233,0.08)' }}>
            <style>{`
              @keyframes pageIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
              .pdf-anim { animation: pageIn 0.3s ease forwards; }
              .react-pdf__Page__canvas { border-radius:8px; box-shadow:0 12px 48px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.07); }
            `}</style>

            {/* Header bar */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3"
              style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <BookOpen size={13} className="text-sky-400 shrink-0" />
                <span className="text-white text-xs font-bold truncate">
                  {lang==='en' ? 'TradeBotHub Pro — Financial OS' : 'TradeBotHub Pro — AI Ecosystem'}
                </span>
                {numPages && <span className="text-gray-600 text-[10px] shrink-0">· {numPages} pgs</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  {[['en','🇺🇸 EN'],['es','🇪🇸 ES']].map(([l,label]) => (
                    <button key={l} onClick={() => { setLang(l); setPage(1); setNumPages(null) }}
                      className="px-2.5 py-1 text-[11px] font-bold transition-all"
                      style={{ background: lang===l ? 'rgba(14,165,233,0.25)' : 'transparent', color: lang===l ? '#38bdf8' : '#6b7280' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <a href={pdfSrc} download
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold hover:opacity-80 transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#9ca3af' }}>
                  <Download size={11} /> PDF
                </a>
              </div>
            </div>

            {/* PDF render area */}
            <div ref={containerRef} className="flex items-center justify-center py-8 px-4 overflow-hidden"
              style={{ background: 'rgba(0,0,0,0.25)', minHeight: 420 }}>
              <Document
                key={pdfSrc}
                file={pdfSrc}
                onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPage(1) }}
                loading={
                  <div className="flex flex-col items-center gap-3 py-20 text-gray-500">
                    <div className="w-8 h-8 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" />
                    <span className="text-xs">Loading document…</span>
                  </div>
                }
                error={
                  <div className="flex flex-col items-center gap-3 py-20">
                    <p className="text-gray-400 text-sm">Preview unavailable.</p>
                    <a href={pdfSrc} download className="text-sky-400 text-xs underline flex items-center gap-1">
                      <Download size={12} /> Download PDF
                    </a>
                  </div>
                }
              >
                <div key={animKey} className="pdf-anim">
                  <Page pageNumber={page} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                </div>
              </Document>
            </div>

            {/* Navigation footer */}
            <div className="flex items-center justify-between gap-3 px-5 py-3"
              style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={() => goTo(page - 1)} disabled={page <= 1}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-20 hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#e5e7eb' }}>
                <ChevronRight size={13} className="rotate-180" /> Prev
              </button>

              <div className="flex flex-col items-center gap-1.5">
                <span className="text-gray-400 text-xs font-semibold tabular-nums">
                  {page} <span className="text-gray-600">/ {numPages ?? '—'}</span>
                </span>
                {numPages && (
                  <div className="flex gap-1 flex-wrap justify-center max-w-[200px]">
                    {Array.from({ length: Math.min(numPages, 14) }, (_, i) => (
                      <button key={i} onClick={() => goTo(i + 1)}
                        className="rounded-full transition-all duration-200"
                        style={{
                          width: page === i + 1 ? 14 : 5, height: 5,
                          background: page === i + 1 ? '#38bdf8' : 'rgba(255,255,255,0.15)',
                        }} />
                    ))}
                    {numPages > 14 && <span className="text-gray-600 text-[9px] leading-[5px]">+{numPages-14}</span>}
                  </div>
                )}
              </div>

              <button onClick={() => goTo(page + 1)} disabled={!numPages || page >= numPages}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-20 hover:opacity-80"
                style={{ background: 'rgba(14,165,233,0.15)', color: '#38bdf8', border: '1px solid rgba(14,165,233,0.2)' }}>
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── Price Ticker ────────────────────────────────────────────────────────────
function PriceTicker({ prices }) {
  return (
    <div className="relative overflow-hidden" style={{ position: 'fixed', top: 64, left: 0, right: 0, zIndex: 40, background: 'rgba(5,5,16,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)', height: 44 }}>
      {/* Edge fade masks */}
      <div className="absolute left-0 top-0 h-full w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #050510 30%, transparent)' }} />
      <div className="absolute right-0 top-0 h-full w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #050510 30%, transparent)' }} />
      {/* Scrolling row — duplicated for seamless loop */}
      <div className="flex items-center h-full" style={{ animation: 'priceScroll 40s linear infinite', width: 'max-content' }}>
        {[...MARKET_COINS, ...MARKET_COINS].map((coin, i) => {
          const p = prices[coin.sym]
          const isUp = (p?.change ?? 0) >= 0
          return (
            <span key={i} className="inline-flex items-center gap-2 px-5 shrink-0 select-none">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: coin.color }} />
              <span className="text-gray-400 text-xs font-semibold">{coin.label}</span>
              <span className="text-white text-xs font-mono tracking-tight">{fmtPrice(p?.price)}</span>
              <span className="text-[11px] font-bold" style={{ color: isUp ? '#00d395' : '#ff4d4f' }}>
                {isUp ? '▲' : '▼'} {Math.abs(p?.change ?? 0).toFixed(2)}%
              </span>
              <span className="text-gray-700 text-xs pl-3">│</span>
            </span>
          )
        })}
      </div>
      <style>{`@keyframes priceScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  )
}

// ─── Live Markets Section ─────────────────────────────────────────────────────
function LiveMarketsSection({ prices }) {
  return (
    <section className="py-12 sm:py-14" style={{ background: '#070714' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal>
          <div className="flex flex-wrap items-center gap-3 mb-7">
            <div className="flex items-center gap-2">
              <span className="relative flex w-2.5 h-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#00d395' }} />
                <span className="relative inline-flex rounded-full w-2.5 h-2.5" style={{ background: '#00d395' }} />
              </span>
              <span className="text-white text-xs font-black tracking-widest">LIVE MARKETS</span>
            </div>
            <span className="text-gray-600 text-[10px] hidden sm:inline">Real-time via Binance · Updates every second</span>
          </div>
        </Reveal>

        {/* Price cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {MARKET_COINS.map((coin, i) => {
            const p = prices[coin.sym]
            const isUp = (p?.change ?? 0) >= 0
            const pct = p?.high > p?.low
              ? Math.round(((p.price - p.low) / (p.high - p.low)) * 100)
              : 50
            return (
              <Reveal key={coin.sym} delay={i * 45}>
                <div
                  className="rounded-2xl p-4 transition-all duration-200 hover:-translate-y-1 cursor-default group"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = coin.color + '50' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)' }}
                >
                  {/* Coin identity + change badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black shrink-0"
                        style={{ background: coin.color + '22', border: `1.5px solid ${coin.color}55`, color: coin.color }}
                      >
                        {coin.label[0]}
                      </div>
                      <div>
                        <p className="text-white text-xs font-bold leading-tight">{coin.label}</p>
                        <p className="text-gray-600 text-[9px]">{coin.name}</p>
                      </div>
                    </div>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0"
                      style={{ background: isUp ? 'rgba(0,211,149,0.12)' : 'rgba(255,77,79,0.12)', color: isUp ? '#00d395' : '#ff4d4f' }}
                    >
                      {isUp ? '+' : ''}{(p?.change ?? 0).toFixed(2)}%
                    </span>
                  </div>

                  {/* Live price */}
                  <p className="text-white text-sm font-extrabold font-mono mb-3 tabular-nums">{fmtPrice(p?.price)}</p>

                  {/* 24h range bar */}
                  {p?.high != null && p?.low != null && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-[9px] text-gray-600">L {fmtPrice(p.low)}</span>
                        <span className="text-[9px] text-gray-600">H {fmtPrice(p.high)}</span>
                      </div>
                      <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${Math.max(4, Math.min(pct, 96))}%`,
                            background: isUp
                              ? 'linear-gradient(90deg,#00d395,#00a878)'
                              : 'linear-gradient(90deg,#ff4d4f,#cc3d3f)',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ─── Navbar ──────────────────────────────────────────────────────────────────
function Navbar({ onOpenModal = () => {} }) {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])
  // close menu when resizing to desktop
  useEffect(() => {
    const fn = () => { if (window.innerWidth >= 768) setOpen(false) }
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  const links = ['Features', 'Strategies', 'Marketplace', 'Pricing', 'Academy']

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(5,5,16,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            {/* Logo */}
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                <Zap size={15} className="text-white" />
              </div>
              <div className="leading-tight">
                <span className="font-bold text-white text-sm sm:text-base">TradeBotHub</span>
                <span className="ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded"
                  style={{ background: 'linear-gradient(90deg,#7c3aed,#4f46e5)', color: '#fff' }}>PRO</span>
              </div>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {links.map(l => (
                <a key={l} href="#" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">{l}</a>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" className="text-gray-300 hover:text-white text-sm font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5">Log in</Link>
              <button
                onClick={onOpenModal}
                className="text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all hover:opacity-90 hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 20px rgba(124,58,237,0.35)' }}>
                Get Started
              </button>
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setOpen(o => !o)} className="md:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className="md:hidden overflow-hidden transition-all duration-300"
          style={{ maxHeight: open ? '400px' : '0', opacity: open ? 1 : 0 }}
        >
          <div style={{ background: 'rgba(8,8,22,0.98)', borderTop: '1px solid rgba(255,255,255,0.06)' }}
            className="px-4 py-4 space-y-1">
            {links.map(l => (
              <a key={l} href="#" onClick={() => setOpen(false)}
                className="block text-gray-300 hover:text-white text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors">
                {l}
              </a>
            ))}
            <div className="pt-3 pb-1 flex flex-col gap-2">
              <Link to="/login" onClick={() => setOpen(false)}
                className="block text-center text-gray-300 hover:text-white text-sm font-medium py-2.5 px-3 rounded-lg border border-white/10 hover:border-white/20 transition-all">
                Log in
              </Link>
              <button onClick={() => { setOpen(false); onOpenModal() }}
                className="block w-full text-center text-white text-sm font-semibold py-2.5 px-3 rounded-lg transition-all"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

// ─── Dashboard Mockup (Hero Right) ───────────────────────────────────────────
function DashboardMockup() {
  const miniData = PORTFOLIO_STATIC.slice(-12)
  return (
    <div className="relative w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl mx-auto lg:mx-0 animate-float"
      style={{ filter: 'drop-shadow(0 30px 60px rgba(124,58,237,0.35))' }}>
      {/* Main card */}
      <div className="rounded-2xl overflow-hidden"
        style={{ background: 'rgba(15,12,35,0.9)', border: '1px solid rgba(124,58,237,0.25)', backdropFilter: 'blur(20px)' }}>
        {/* Header bar */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3"
          style={{ background: 'rgba(124,58,237,0.1)', borderBottom: '1px solid rgba(124,58,237,0.15)' }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
              <Zap size={10} className="text-white" />
            </div>
            <span className="text-white text-xs font-bold">TradeBotHub PRO</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-ping-slow" />
            <span className="text-green-400 text-xs font-medium">Live</span>
          </div>
        </div>
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-0 px-3 sm:px-4 py-2.5 sm:py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {[
            { label: 'Total Balance', val: '$128,645', sub: '↑ 8.45%', c: '#00d395' },
            { label: 'Win Rate', val: '68.43%', sub: '↑ 2.8%', c: '#00d395' },
            { label: 'Active Bots', val: '12', sub: 'running', c: '#8b5cf6' },
          ].map(s => (
            <div key={s.label} className="px-1 sm:px-2">
              <p className="text-gray-500 text-[9px] sm:text-[10px] mb-0.5">{s.label}</p>
              <p className="text-white text-xs sm:text-sm font-bold leading-tight">{s.val}</p>
              <p className="text-[9px] sm:text-[10px] font-medium" style={{ color: s.c }}>{s.sub}</p>
            </div>
          ))}
        </div>
        {/* Chart */}
        <div className="px-1 sm:px-2 pt-2 pb-1">
          <p className="text-gray-400 text-[10px] px-2 mb-1">Portfolio Performance</p>
          <ResponsiveContainer width="100%" height={80}>
            <AreaChart data={miniData} margin={{ top: 2, right: 2, left: -30, bottom: 0 }}>
              <defs>
                <linearGradient id="mgGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis hide />
              <Area type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={1.5} fill="url(#mgGrad)" dot={false} animationDuration={2000} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Recent signals */}
        <div className="px-3 sm:px-4 pb-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-gray-400 text-[10px] py-2">Recent Signals</p>
          <div className="space-y-1.5">
            {[
              { pair: 'BTC/USDT', dir: 'Long', pnl: '+$1,250.75', color: '#00d395' },
              { pair: 'ETH/USDT', dir: 'Short', pnl: '+$832.21', color: '#00d395' },
              { pair: 'SOL/USDT', dir: 'Long', pnl: '+$1,230.75', color: '#00d395' },
            ].map(s => (
              <div key={s.pair} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-white text-[10px] sm:text-xs font-medium">{s.pair}</span>
                  <span className="text-[8px] sm:text-[10px] px-1 rounded" style={{ background: 'rgba(0,211,149,0.15)', color: '#00d395' }}>{s.dir}</span>
                </div>
                <span className="text-[10px] sm:text-xs font-bold" style={{ color: s.color }}>{s.pnl}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating bot card */}
      <div className="absolute -right-2 sm:-right-6 top-6 sm:top-10 rounded-xl px-3 py-2 animate-float-slow hidden sm:block"
        style={{ background: 'rgba(10,6,30,0.95)', border: '1px solid rgba(124,58,237,0.3)', backdropFilter: 'blur(16px)', boxShadow: '0 0 25px rgba(124,58,237,0.25)' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
            <Bot size={12} className="text-white" />
          </div>
          <div>
            <p className="text-white text-[10px] font-bold">AI Trend Hunter</p>
            <p className="text-[9px]" style={{ color: '#00d395' }}>+12.45% · Running</p>
          </div>
        </div>
      </div>

      {/* Floating notification */}
      <div className="absolute -left-2 sm:-left-8 bottom-10 sm:bottom-16 rounded-xl px-3 py-2 animate-float-fast hidden sm:block"
        style={{ background: 'rgba(10,6,30,0.95)', border: '1px solid rgba(0,211,149,0.3)', backdropFilter: 'blur(16px)', boxShadow: '0 0 25px rgba(0,211,149,0.2)' }}>
        <div className="flex items-center gap-2">
          <Bell size={12} style={{ color: '#00d395' }} />
          <div>
            <p className="text-white text-[10px] font-bold">Bot Alert</p>
            <p className="text-[9px]" style={{ color: '#00d395' }}>SOL/USDT +$1,230.75</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────────
function HeroSection({ onOpenModal = () => {} }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#050510', paddingTop: '108px' }}>
      <ParticleCanvas />
      {/* Gradient orbs */}
      <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 rounded-full pointer-events-none animate-glow-pulse"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)', filter: 'blur(50px)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-72 h-48 sm:h-72 rounded-full pointer-events-none animate-glow-pulse"
        style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.2) 0%, transparent 70%)', filter: 'blur(50px)', animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-0 w-40 sm:w-64 h-40 sm:h-64 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(rgba(124,58,237,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-16 lg:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 xl:gap-24">
          {/* Left — text */}
          <div className="flex-1 text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
            {/* Badge */}
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 sm:mb-6 text-xs font-semibold"
                style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#a78bfa' }}>
                <Sparkles size={12} className="text-violet-400" />
                AUTOMATE. TRADE. SCALE.
              </div>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight mb-4 sm:mb-5">
                The All-in-One Platform for{' '}
                <span className="animate-gradient-x bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(90deg,#a78bfa,#7c3aed,#4f46e5,#06b6d4)', backgroundSize: '200% auto' }}>
                  Intelligent
                </span>{' '}
                Algorithmic Trading
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0">
                Create, test, and deploy trading bots. Connect your exchanges, automate strategies, buy and sell signals, and learn algorithmic trading — all in one place.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8 sm:mb-10">
                <button
                  onClick={onOpenModal}
                  className="flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-xl text-sm sm:text-base transition-all hover:opacity-90 hover:translate-y-[-1px] hover:shadow-2xl group"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 30px rgba(124,58,237,0.4)' }}>
                  Start Free Trial
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <Link to="/dashboard"
                  className="flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-xl text-sm sm:text-base transition-all hover:bg-white/10"
                  style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' }}>
                  <Play size={14} className="fill-white" />
                  Explore Platform
                </Link>
              </div>
            </Reveal>

            {/* Trust badges */}
            <Reveal delay={320}>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 justify-center lg:justify-start text-xs sm:text-sm text-gray-500">
                {['No credit card required', '7-day free access', 'Cancel anytime'].map(t => (
                  <div key={t} className="flex items-center gap-1.5">
                    <CheckCircle size={12} className="text-violet-500 shrink-0" />
                    {t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right — dashboard mockup */}
          <Reveal className="flex-1 w-full" delay={100} direction="right">
            <DashboardMockup />
          </Reveal>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #050510)' }} />
    </section>
  )
}

// ─── Stats ────────────────────────────────────────────────────────────────────
function StatsSection() {
  const stats = [
    { label: 'Active Traders', val: 25000, suffix: '+', icon: Users },
    { label: 'Volume Automated', prefix: '$', val: 2.3, suffix: 'B+', decimals: 1, icon: DollarSign },
    { label: 'Strategies Backtested', val: 120000, suffix: '+', icon: Activity },
    { label: 'Uptime & Reliability', val: 98.6, suffix: '%', decimals: 1, icon: Award },
  ]
  return (
    <section className="relative py-12 sm:py-14" style={{ background: '#070714', borderTop: '1px solid rgba(124,58,237,0.1)', borderBottom: '1px solid rgba(124,58,237,0.1)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-semibold tracking-widest text-gray-500 mb-8">TRUSTED BY TRADERS WORLDWIDE</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {stats.map(({ label, val, suffix, prefix, decimals, icon: Icon }) => (
            <Reveal key={label} className="text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                  <Icon size={18} className="text-violet-400" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white mb-1">
                <Counter target={val} prefix={prefix} suffix={suffix} decimals={decimals} />
              </div>
              <p className="text-gray-500 text-xs sm:text-sm">{label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Features ─────────────────────────────────────────────────────────────────
function FeaturesSection() {
  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#050510' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-12 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3">Everything You Need to Automate and Grow</h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">Powerful tools. Real-time data. Advanced automation.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {FEATURES.map(({ icon: Icon, title, desc, color }, i) => (
            <Reveal key={title} delay={i * 70}>
              <div className="group relative rounded-2xl p-5 sm:p-6 cursor-pointer h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = color + '55'; e.currentTarget.style.background = color + '08' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}>
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: color + '18', border: `1px solid ${color}30` }}>
                  <Icon size={22} style={{ color }} />
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                <div className="flex items-center gap-1 mt-4 text-xs font-semibold transition-all group-hover:gap-2" style={{ color }}>
                  Learn more <ChevronRight size={12} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Strategy Builder ─────────────────────────────────────────────────────────
function StrategySection() {
  const [activeStep, setActiveStep] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setActiveStep(s => (s + 1) % 4), 1800)
    return () => clearInterval(t)
  }, [])

  const steps = [
    { label: 'EMA Crossover', sub: 'Indicator', color: '#7c3aed' },
    { label: 'RSI Filter', sub: 'Condition', color: '#2563eb' },
    { label: 'Risk Mgmt', sub: '1% per trade', color: '#ea580c' },
    { label: 'Execute Order', sub: 'Long / Short', color: '#00d395' },
  ]

  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#070714' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left */}
          <Reveal className="flex-1 text-center lg:text-left" direction="left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold"
              style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>
              <Cpu size={12} /> VISUAL STRATEGY BUILDER
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
              Powerful Strategy Builder
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
              Build advanced strategies using our visual editor. No coding required. Drag, drop, and connect indicators, conditions, and actions in a intuitive flowchart.
            </p>
            <Link to="/register"
              className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 20px rgba(124,58,237,0.35)' }}>
              Start Building <ArrowRight size={14} />
            </Link>
          </Reveal>

          {/* Right — animated flowchart */}
          <Reveal className="flex-1 w-full" delay={100} direction="right">
            <div className="rounded-2xl p-6 sm:p-8 max-w-md mx-auto"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-0">
                {steps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div
                      className="rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-center transition-all duration-500 min-w-[70px] sm:min-w-[80px]"
                      style={{
                        background: activeStep === i ? s.color + '25' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${activeStep === i ? s.color : 'rgba(255,255,255,0.08)'}`,
                        boxShadow: activeStep === i ? `0 0 20px ${s.color}40` : 'none',
                        transform: activeStep === i ? 'scale(1.05)' : 'scale(1)',
                      }}>
                      <p className="text-xs sm:text-sm font-bold text-white leading-tight">{s.label}</p>
                      <p className="text-[9px] sm:text-[10px] mt-0.5" style={{ color: s.color }}>{s.sub}</p>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="flex items-center justify-center w-4 sm:w-8 shrink-0">
                        <div className="h-px flex-1 transition-all duration-500"
                          style={{ background: activeStep > i ? '#7c3aed' : 'rgba(255,255,255,0.12)', boxShadow: activeStep > i ? '0 0 6px #7c3aed' : 'none' }} />
                        <ChevronRight size={10} className="text-gray-600 shrink-0" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-center">
                <div className="rounded-xl px-8 py-3 text-center"
                  style={{ background: activeStep === 3 ? 'rgba(0,211,149,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${activeStep === 3 ? '#00d395' : 'rgba(255,255,255,0.08)'}`, boxShadow: activeStep === 3 ? '0 0 20px rgba(0,211,149,0.3)' : 'none', transition: 'all 0.5s' }}>
                  <p className="text-xs font-bold text-white">Execute Order</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#00d395' }}>Long / Short</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── AI Assistant ─────────────────────────────────────────────────────────────
function AISection() {
  const msgs = [
    { from: 'user', text: 'Analyze my EMA strategy performance' },
    { from: 'ai', text: 'Your EMA 20/50 strategy shows a 68.4% win rate over the last 30 days with a Sharpe ratio of 1.82. Strong performance!' },
    { from: 'user', text: 'Optimize for lower drawdown' },
    { from: 'ai', text: 'Adding an ATR-based trailing stop (2x ATR) could reduce max drawdown by ~35% while preserving 91% of returns.' },
  ]
  const [shown, setShown] = useState(1)
  useEffect(() => {
    if (shown < msgs.length) {
      const t = setTimeout(() => setShown(s => s + 1), 1200)
      return () => clearTimeout(t)
    }
  }, [shown, msgs.length])
  const [ref, visible] = useReveal()
  useEffect(() => { if (visible) setShown(1) }, [visible])

  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#050510' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16">
          {/* Right — image */}
          <Reveal className="flex-1 flex justify-center" direction="right">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute inset-0 rounded-full blur-3xl"
                style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.4) 0%, transparent 70%)' }} />
              <img src="/images/ai-assistant.png" alt="AI Assistant" className="relative w-full rounded-3xl"
                style={{ filter: 'drop-shadow(0 0 40px rgba(124,58,237,0.5))' }} />
            </div>
          </Reveal>

          {/* Left — chat */}
          <div ref={ref} className="flex-1 w-full max-w-lg mx-auto lg:mx-0">
            <Reveal direction="left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold"
                style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>
                <Sparkles size={12} /> AI TRADING ASSISTANT
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
                Your Intelligent Trading Companion
              </h2>
              <p className="text-gray-400 text-sm sm:text-base mb-6 max-w-lg">
                Analyze strategies, optimize bots, and execute better trades with our advanced AI assistant trained on millions of market data points.
              </p>
            </Reveal>

            {/* Chat interface */}
            <div className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center gap-3 px-4 py-3"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(124,58,237,0.08)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}>
                  <Sparkles size={12} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">AI Assistant</p>
                  <p className="text-[10px] text-green-400">Online · Powered by GPT-4</p>
                </div>
              </div>
              <div className="p-4 space-y-3 min-h-[180px]">
                {msgs.slice(0, shown).map((m, i) => (
                  <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed"
                      style={{
                        background: m.from === 'user' ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'rgba(255,255,255,0.06)',
                        color: '#f1f5f9',
                        animation: 'slide-up 0.3s ease-out',
                      }}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {shown < msgs.length && (
                  <div className="flex justify-start">
                    <div className="flex gap-1 px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      {[0, 1, 2].map(d => (
                        <div key={d} className="w-1.5 h-1.5 rounded-full bg-gray-400"
                          style={{ animation: `bounce 1s ease-in-out ${d * 0.2}s infinite` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-4 pb-4">
                <div className="flex gap-2 flex-wrap">
                  {['Analyze my strategy →', 'Optimize this bot →', 'Generate new strategy →'].map(s => (
                    <button key={s} className="text-[10px] px-3 py-1.5 rounded-lg transition-colors text-violet-300 hover:text-white"
                      style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Live Performance ─────────────────────────────────────────────────────────
function LivePerformanceSection() {
  const [data, setData] = useState(() => genPrice(50))
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => {
      setData(prev => {
        const last = prev[prev.length - 1]
        const np = Math.round(last.price + (Math.random() - 0.46) * 320)
        const newPt = { i: last.i + 1, price: Math.max(np, 38000) }
        return [...prev.slice(-59), newPt]
      })
      setTick(n => n + 1)
    }, 1500)
    return () => clearInterval(t)
  }, [])

  const first = data[0]?.price ?? 43500
  const last = data[data.length - 1]?.price ?? first
  const diff = last - first, pct = (diff / first * 100).toFixed(2)
  const isUp = diff >= 0

  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#070714' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3">Live Performance</h2>
          <p className="text-gray-400 text-sm sm:text-base">Real-time analytics and performance tracking for all your bots and strategies.</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Chart — left 2/3 */}
          <Reveal className="lg:col-span-2" direction="left">
            <div className="rounded-2xl p-4 sm:p-6 h-full"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-bold text-sm sm:text-base">BTC/USDT Live</h3>
                    <span className="px-2 py-0.5 rounded text-xs font-bold"
                      style={{ background: 'rgba(0,211,149,0.12)', color: '#00d395' }}>● LIVE</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-white font-extrabold text-xl sm:text-2xl">${last.toLocaleString()}</span>
                    <span className={`text-sm font-bold flex items-center gap-1 ${isUp ? 'text-trading-green' : 'text-trading-red'}`}>
                      {isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {isUp ? '+' : ''}{pct}%
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {['1M', '5M', '1H', '1D'].map(tf => (
                    <button key={tf} className="px-2 py-1 rounded text-xs font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-colors">{tf}</button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
                  <defs>
                    <linearGradient id="liveGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={isUp ? '#00d395' : '#ff4d4f'} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={isUp ? '#00d395' : '#ff4d4f'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="i" hide />
                  <YAxis tick={{ fill: '#4b5563', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="price" stroke={isUp ? '#00d395' : '#ff4d4f'} strokeWidth={2} fill="url(#liveGrad)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Reveal>

          {/* Bots table — right 1/3 */}
          <Reveal direction="right">
            <div className="rounded-2xl p-4 sm:p-6 h-full"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="text-white font-bold text-sm sm:text-base mb-4">Top Performing Bots</h3>
              <div className="space-y-3">
                {BOT_PERFORMANCE.map((b, i) => (
                  <div key={b.name} className="flex items-center justify-between py-2"
                    style={{ borderBottom: i < BOT_PERFORMANCE.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                        <Bot size={10} className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs font-medium truncate">{b.name}</p>
                        <p className="text-gray-500 text-[10px]">Running</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className={`text-xs font-bold ${b.pos ? 'text-trading-green' : 'text-trading-red'}`}>{b.pct}</p>
                      <p className="text-gray-500 text-[10px]">{b.pnl}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/register" className="mt-4 block text-center text-xs font-semibold py-2.5 rounded-xl transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                <span className="text-white">View All Reports →</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── Backtesting ──────────────────────────────────────────────────────────────
function BacktestingSection({ onOpenModal = () => {} }) {
  const stats = [
    { label: 'Net Profit',    value: '+$23,450.75', color: '#00d395' },
    { label: 'Profit Factor', value: '1.82',        color: '#a78bfa' },
    { label: 'Max Drawdown',  value: '-12.45%',     color: '#ff4d4f' },
    { label: 'Win Rate',      value: '64.32%',      color: '#00d395' },
  ]
  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#050510' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Chart */}
          <Reveal className="flex-1 w-full" direction="left">
            <div className="rounded-2xl p-5 sm:p-6"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-sm">BTC/USDT · EMA Crossover · 90 Days</h3>
                <span className="text-xs px-2 py-0.5 rounded font-bold"
                  style={{ background: 'rgba(0,211,149,0.12)', color: '#00d395' }}>+23.4%</span>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={BACKTEST_EQUITY} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
                  <defs>
                    <linearGradient id="btGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="i" hide />
                  <YAxis tick={{ fill: '#4b5563', fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="value" stroke="#7c3aed" strokeWidth={2}
                    fill="url(#btGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {stats.map(s => (
                  <div key={s.label} className="rounded-xl p-2.5 text-center"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-[10px] text-gray-500 mb-1">{s.label}</p>
                    <p className="text-sm font-extrabold" style={{ color: s.color }}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Text */}
          <Reveal className="flex-1 text-center lg:text-left" delay={100} direction="right">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.25)', color: '#60a5fa' }}>
              <BarChart2 size={12} /> BACKTESTING ENGINE
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
              Backtesting Engine
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
              Test your strategies against years of real historical data before risking any capital.
              Get professional-grade analytics on every trade, drawdown, and risk metric.
            </p>
            <div className="space-y-2.5 mb-6 text-left max-w-xs mx-auto lg:mx-0">
              {['Historical data from 50+ exchanges', 'Detailed trade-by-trade reports', 'Monte Carlo risk simulation', 'Portfolio-level backtesting'].map(f => (
                <div key={f} className="flex items-center gap-2 text-xs text-gray-300">
                  <CheckCircle size={13} className="text-blue-400 shrink-0" />
                  {f}
                </div>
              ))}
            </div>
            <button
              onClick={() => onOpenModal('pro')}
              className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-90 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)', boxShadow: '0 0 24px rgba(37,99,235,0.4)' }}
            >
              Start Backtesting <ArrowRight size={14} />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── Signal Marketplace ───────────────────────────────────────────────────────
function SignalMarketplaceSection({ onOpenModal = () => {} }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#070714' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Text + signal list */}
          <Reveal className="flex-1" direction="left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold"
              style={{ background: 'rgba(5,150,105,0.12)', border: '1px solid rgba(5,150,105,0.25)', color: '#34d399' }}>
              <Signal size={12} /> SIGNAL MARKETPLACE
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight text-center lg:text-left">
              Signal Marketplace
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0 text-center lg:text-left">
              Buy, sell and follow profitable signals from verified top traders. Copy expert strategies automatically with one click.
            </p>

            <div className="space-y-3 mb-6">
              {SIGNALS.map((sig, i) => (
                <Reveal key={sig.name} delay={i * 80}>
                  <div
                    className="flex items-center justify-between rounded-2xl p-3 sm:p-4 transition-all hover:-translate-y-0.5 cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = sig.color + '55' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)' }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 font-extrabold text-sm"
                        style={{ background: `${sig.color}20`, border: `1.5px solid ${sig.color}55`, color: sig.color }}>
                        {sig.name[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs sm:text-sm font-bold truncate">{sig.name}</p>
                        <p className="text-gray-500 text-[10px]">{sig.followers} followers · {sig.trades} trades</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                      {/* Mini sparkline */}
                      <div className="hidden sm:block">
                        <ResponsiveContainer width={60} height={28}>
                          <LineChart data={sig.spark.map((v, j) => ({ j, v }))}>
                            <Line type="monotone" dataKey="v" stroke={sig.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-extrabold" style={{ color: sig.color }}>{sig.pct}</p>
                        <p className="text-gray-500 text-[10px]">{sig.winRate} win rate</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="text-center lg:text-left">
              <button
                onClick={() => onOpenModal('pro')}
                className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-90 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg,#059669,#0891b2)', boxShadow: '0 0 24px rgba(5,150,105,0.35)' }}
              >
                <ShoppingBag size={14} /> Explore Marketplace
              </button>
            </div>
          </Reveal>

          {/* Phone mockup */}
          <Reveal className="shrink-0 hidden lg:flex items-center justify-center" delay={100} direction="right">
            <div className="relative" style={{ width: 200, height: 390 }}>
              {/* Glow */}
              <div className="absolute inset-0 rounded-[36px] opacity-30 blur-2xl"
                style={{ background: 'radial-gradient(circle, rgba(5,150,105,0.8) 0%, transparent 70%)' }} />
              {/* Frame */}
              <div className="absolute inset-0 rounded-[36px]"
                style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))', border: '2px solid rgba(255,255,255,0.12)', boxShadow: '0 0 60px rgba(5,150,105,0.2), inset 0 1px 0 rgba(255,255,255,0.1)' }} />
              {/* Notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-1.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.15)' }} />
              {/* Screen */}
              <div className="absolute inset-0 m-2 rounded-[30px] overflow-hidden p-3 pt-8"
                style={{ background: '#06060f' }}>
                <p className="text-white text-[10px] font-black mb-2.5 tracking-wide">Signal Details</p>
                {/* Top card */}
                <div className="rounded-xl p-2.5 mb-2.5"
                  style={{ background: 'rgba(0,211,149,0.1)', border: '1px solid rgba(0,211,149,0.3)' }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black"
                      style={{ background: 'rgba(0,211,149,0.3)', color: '#00d395' }}>A</div>
                    <p className="text-[9px] text-white font-semibold">Alpha Signals</p>
                    <span className="ml-auto text-[7px] px-1 rounded" style={{ background: 'rgba(0,211,149,0.2)', color: '#00d395' }}>LIVE</span>
                  </div>
                  <p className="text-[22px] font-extrabold text-center leading-none" style={{ color: '#00d395' }}>+24.35%</p>
                  <div className="flex justify-between mt-2">
                    {[['Win Rate', '72.4%'], ['Trades', '156'], ['Followers', '2.3K']].map(([k, v]) => (
                      <div key={k} className="text-center">
                        <p className="text-[7px] text-gray-500">{k}</p>
                        <p className="text-[9px] font-bold text-white">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Signal list */}
                <div className="space-y-1.5">
                  {[{ n: 'Crypto Whale', p: '+18.67%', c: '#a78bfa' }, { n: 'Momentum Pro', p: '+15.23%', c: '#60a5fa' }].map(s => (
                    <div key={s.n} className="flex items-center justify-between rounded-lg px-2 py-1.5"
                      style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full" style={{ background: s.c + '55' }} />
                        <p className="text-[8px] text-white">{s.n}</p>
                      </div>
                      <p className="text-[9px] font-bold" style={{ color: s.c }}>{s.p}</p>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-3 py-2 rounded-xl text-[9px] font-black text-white"
                  style={{ background: 'linear-gradient(135deg,#059669,#0891b2)' }}>
                  Subscribe
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── Academy ──────────────────────────────────────────────────────────────────
function AcademySection({ onOpenModal = () => {} }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#050510' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Text + course list */}
          <Reveal className="flex-1" direction="left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold"
              style={{ background: 'rgba(147,51,234,0.12)', border: '1px solid rgba(147,51,234,0.25)', color: '#c084fc' }}>
              <GraduationCap size={12} /> TRADING ACADEMY
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight text-center lg:text-left">
              Academy & Learning
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0 text-center lg:text-left">
              Learn algorithmic trading from beginner to advanced. Expert-led courses covering every aspect of automated trading, risk management, and strategy development.
            </p>
            <div className="space-y-2.5 mb-6">
              {COURSES.map((c, i) => {
                const CIcon = c.icon
                return (
                  <Reveal key={c.label} delay={i * 60}>
                    <div
                      className="flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer transition-all hover:translate-x-1"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = c.color + '55'; e.currentTarget.style.background = c.color + '08' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: c.color + '20', border: `1px solid ${c.color}40` }}>
                        <CIcon size={13} style={{ color: c.color }} />
                      </div>
                      <p className="text-white text-xs sm:text-sm font-medium flex-1">{c.label}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0"
                        style={{ background: c.color + '15', color: c.color }}>{c.level}</span>
                      <ChevronRight size={12} className="text-gray-600 shrink-0" />
                    </div>
                  </Reveal>
                )
              })}
            </div>
            <div className="text-center lg:text-left">
              <button
                onClick={() => onOpenModal('starter')}
                className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-90 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg,#9333ea,#7c3aed)', boxShadow: '0 0 24px rgba(147,51,234,0.35)' }}
              >
                <BookOpen size={14} /> Start Learning Free
              </button>
            </div>
          </Reveal>

          {/* Decorative visual */}
          <Reveal className="flex-1 flex items-center justify-center hidden lg:flex" delay={100} direction="right">
            <div className="relative w-72 h-72">
              {/* Radial glow */}
              <div className="absolute inset-0 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(147,51,234,0.18) 0%, transparent 65%)' }} />
              {/* Center */}
              <div className="absolute inset-12 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(147,51,234,0.08)', border: '2px solid rgba(147,51,234,0.25)', boxShadow: '0 0 60px rgba(147,51,234,0.2)' }}>
                <GraduationCap size={56} style={{ color: 'rgba(192,132,252,0.85)' }} />
              </div>
              {/* Orbiting badges */}
              {COURSES.map((c, i) => {
                const angle = (i * (360 / COURSES.length) - 90) * (Math.PI / 180)
                const rx = 42, ry = 42
                const x = 50 + rx * Math.cos(angle)
                const y = 50 + ry * Math.sin(angle)
                const CIcon = c.icon
                return (
                  <div
                    key={c.label}
                    className="absolute -translate-x-1/2 -translate-y-1/2 rounded-xl px-2.5 py-1.5 flex items-center gap-1.5 whitespace-nowrap"
                    style={{ left: `${x}%`, top: `${y}%`, background: 'rgba(8,8,20,0.95)', border: `1px solid ${c.color}55`, boxShadow: `0 0 12px ${c.color}20` }}
                  >
                    <CIcon size={11} style={{ color: c.color }} />
                    <span className="text-[9px] font-semibold text-white">{c.label.split(' ').slice(0, 2).join(' ')}</span>
                  </div>
                )
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── Security ─────────────────────────────────────────────────────────────────
function SecuritySection() {
  const badges = [
    { icon: Shield,       label: 'Your Funds. Your Control.', desc: 'We never take custody of your funds.' },
    { icon: Lock,         label: 'Bank-Level Security',       desc: 'Encrypted connections and industry-leading protection.' },
    { icon: CheckCircle,  label: 'No Withdrawal Permissions', desc: 'Trade safely. We only need trade access.' },
    { icon: Bot,          label: '24/7 Automation',           desc: 'Bots run round-the-clock so you never miss an opportunity.' },
    { icon: Users,        label: 'Community & Support',       desc: 'Join a global community of traders and builders.' },
    { icon: TrendingUp,   label: 'Built for Traders',         desc: 'Designed by traders, for traders.' },
  ]
  return (
    <section className="py-12 sm:py-16"
      style={{ background: '#070714', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-center text-xs font-semibold tracking-widest text-gray-500 mb-8">TRUSTED. SECURE. RELIABLE.</p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
          {badges.map(({ icon: Icon, label, desc }, i) => (
            <Reveal key={label} delay={i * 50}>
              <div className="flex flex-col items-center text-center gap-2 p-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                  <Icon size={18} className="text-violet-400" />
                </div>
                <p className="text-white text-xs font-bold leading-tight">{label}</p>
                <p className="text-gray-500 text-[10px] leading-snug">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Exchanges ────────────────────────────────────────────────────────────────
function ExchangesSection() {
  const doubled = [...EXCHANGES, ...EXCHANGES]
  return (
    <section className="py-12 sm:py-14" style={{ background: '#070714', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-center text-xs font-semibold tracking-widest text-gray-500 mb-8">CONNECT TO YOUR FAVORITE EXCHANGES</p>
        </Reveal>
        <div className="overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #070714, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #070714, transparent)' }} />
          <div className="flex gap-6 sm:gap-8 animate-scroll-left" style={{ width: 'max-content' }}>
            {doubled.map(({ name, color }, i) => (
              <div key={i} className="rounded-xl px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center shrink-0 transition-all hover:scale-105"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', minWidth: '100px' }}>
                <span className="text-xs sm:text-sm font-extrabold whitespace-nowrap" style={{ color }}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Pricing ─────────────────────────────────────────────────────────────────
const LAUNCH_DATE_P = new Date('2026-06-20T00:00:00')
function getRemP() {
  const d = LAUNCH_DATE_P - new Date()
  if (d <= 0) return { d: 0, h: 0, m: 0, s: 0 }
  return { d: Math.floor(d / 86400000), h: Math.floor((d % 86400000) / 3600000), m: Math.floor((d % 3600000) / 60000), s: Math.floor((d % 60000) / 1000) }
}
const pp = (n) => String(n).padStart(2, '0')

function PricingSection({ onOpenModal = () => {} }) {
  const [rem, setRem] = useState(getRemP)
  useEffect(() => { const t = setInterval(() => setRem(getRemP()), 1000); return () => clearInterval(t) }, [])

  const plans = [
    {
      key: 'starter', label: 'Starter', icon: '🚀', price: 24.50, original: 49,
      desc: 'Perfect to get started with automated trading.',
      features: ['1 Trading Bot', '5 Active Strategies', 'Backtesting Engine', 'Email & SMS Alerts', 'Exchange Integration', '7-day Free Trial'],
      color: '#7c3aed',
    },
    {
      key: 'pro', label: 'Pro', icon: '⚡', price: 49.50, original: 99,
      desc: 'For active traders who want the full toolkit.',
      features: ['5 Trading Bots', '20 Active Strategies', 'Signal Marketplace', 'Real-time Alerts', 'API Access', 'Priority Support', 'Advanced Analytics'],
      color: '#2563eb', popular: true,
    },
    {
      key: 'elite', label: 'Elite', icon: '👑', price: 124.50, original: 249,
      desc: 'Maximum power for professional algo traders.',
      features: ['Unlimited Bots', 'All Strategies', 'Custom APIs', 'White-label Access', 'Dedicated Manager', 'Co-location Support'],
      color: '#06b6d4',
    },
  ]

  return (
    <section id="pricing" className="py-16 sm:py-20 lg:py-28" style={{ background: '#070714' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-10 sm:mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-wider text-white mb-5"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 25px rgba(124,58,237,0.5)' }}>
            <Zap size={10} fill="white" />
            EARLY ACCESS — 50% OFF FOREVER
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3 leading-tight">
            Lock In Your Price Before Launch
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto mb-8">
            Platform launches <strong className="text-white">June 20, 2026</strong>. First 100 subscribers get 50% off forever.
          </p>

          {/* Live countdown */}
          <div className="inline-flex items-center gap-3 sm:gap-4 px-5 sm:px-8 py-4 rounded-2xl mb-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(124,58,237,0.3)', boxShadow: '0 0 30px rgba(124,58,237,0.15)' }}>
            {[{ v: rem.d, l: 'DAYS' }, { v: rem.h, l: 'HRS' }, { v: rem.m, l: 'MIN' }, { v: rem.s, l: 'SEC' }].map((x, i) => (
              <React.Fragment key={x.l}>
                <div className="text-center">
                  <div className="text-white font-black text-2xl sm:text-3xl tabular-nums" style={{ textShadow: '0 0 20px rgba(124,58,237,0.8)' }}>{pp(x.v)}</div>
                  <div className="text-gray-600 text-[9px] font-bold tracking-widest mt-0.5">{x.l}</div>
                </div>
                {i < 3 && <span className="text-violet-500 font-black text-2xl pb-4">:</span>}
              </React.Fragment>
            ))}
          </div>
          <p className="text-orange-400 text-xs font-bold">⚡ Only 37 spots remaining at 50% off</p>
        </Reveal>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {plans.map((plan, i) => (
            <Reveal key={plan.key} delay={i * 100}>
              <div
                className="relative rounded-2xl p-6 sm:p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-2"
                style={{
                  background: plan.popular ? 'linear-gradient(145deg, rgba(37,99,235,0.15), rgba(124,58,237,0.1))' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${plan.popular ? 'rgba(37,99,235,0.5)' : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: plan.popular ? '0 0 50px rgba(37,99,235,0.2)' : 'none',
                }}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-4 py-1 rounded-full text-xs font-black text-white"
                      style={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)' }}>MOST POPULAR</span>
                  </div>
                )}

                <div className="mb-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{plan.icon}</span>
                    <div>
                      <h3 className="text-white font-black text-xl">{plan.label}</h3>
                      <p className="text-gray-500 text-xs">{plan.desc}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-gray-600 line-through text-base">${plan.original}/mo</span>
                      <span className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                        style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>50% OFF</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-white font-black text-4xl sm:text-5xl">${plan.price}</span>
                      <span className="text-gray-500 text-sm">/mo</span>
                    </div>
                  </div>
                  <p className="text-green-400 text-xs mt-1.5 font-semibold">🔒 Locked forever at this price</p>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-gray-300">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: plan.color + '20', border: `1px solid ${plan.color}40` }}>
                        <CheckCircle size={9} style={{ color: plan.color }} />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => onOpenModal(plan.key)}
                  className="w-full py-3.5 rounded-xl font-black text-sm text-white transition-all hover:opacity-90 hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                  style={{
                    background: plan.popular ? 'linear-gradient(135deg,#2563eb,#7c3aed)' : `linear-gradient(135deg,${plan.color},${plan.color}cc)`,
                    boxShadow: `0 0 25px ${plan.color}40`,
                  }}
                >
                  <Zap size={13} fill="white" />
                  Claim Early Access
                  <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-center text-gray-600 text-[10px] mt-2">First month · No auto-charge</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <div className="mt-10 sm:mt-12 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs text-gray-500 px-6 py-4 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {['No credit card required', 'Price locked forever', 'Cancel anytime', 'Priority onboarding at launch'].map(t => (
                <div key={t} className="flex items-center gap-1.5">
                  <CheckCircle size={11} className="text-violet-500 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── CTA ──────────────────────────────────────────────────────────────────────
function CTASection({ onOpenModal = () => {} }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24" style={{ background: '#050510' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden p-8 sm:p-12 lg:p-16"
            style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(79,70,229,0.2) 50%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(124,58,237,0.3)' }}>
            <div className="absolute top-0 left-1/4 w-48 sm:w-64 h-48 sm:h-64 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)', filter: 'blur(50px)' }} />
            <div className="absolute bottom-0 right-1/4 w-48 sm:w-64 h-48 sm:h-64 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)', filter: 'blur(50px)' }} />
            <div className="relative">
              <div className="flex items-center justify-center gap-2 mb-4">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={16} className="fill-yellow-400 text-yellow-400" />)}
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
                Start Your Free Trial Today
              </h2>
              <p className="text-gray-300 text-sm sm:text-base mb-8 max-w-lg mx-auto">
                Join 25,000+ traders. No credit card required. Access all features free for 7 days.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={onOpenModal}
                  className="flex items-center justify-center gap-2 text-white font-semibold px-8 py-3.5 rounded-xl text-base transition-all hover:opacity-90 hover:shadow-2xl group"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 30px rgba(124,58,237,0.5)' }}>
                  Get Started Free
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <Link to="/login"
                  className="flex items-center justify-center gap-2 text-white font-semibold px-8 py-3.5 rounded-xl text-base transition-all hover:bg-white/10"
                  style={{ border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)' }}>
                  Log In
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: 'Product', links: ['Features', 'Strategies', 'Pricing', 'Changelog'] },
    { title: 'Platform', links: ['Bots', 'Backtesting', 'Signals', 'Marketplace'] },
    { title: 'Learn', links: ['Academy', 'Documentation', 'Blog', 'Community'] },
    { title: 'Company', links: ['About', 'Careers', 'Privacy', 'Terms'] },
  ]
  return (
    <footer style={{ background: '#030309', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                <Zap size={15} className="text-white" />
              </div>
              <div>
                <span className="font-bold text-white text-sm">TradeBotHub</span>
                <span className="ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded"
                  style={{ background: 'linear-gradient(90deg,#7c3aed,#4f46e5)', color: '#fff' }}>PRO</span>
              </div>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm leading-relaxed max-w-xs">
              The all-in-one platform for intelligent algorithmic trading. Automate, test, and scale your strategies.
            </p>
          </div>

          {/* Links */}
          {cols.map(c => (
            <div key={c.title}>
              <p className="text-white text-xs font-bold tracking-wide mb-3 sm:mb-4">{c.title}</p>
              <ul className="space-y-2">
                {c.links.map(l => (
                  <li key={l}>
                    <a href="#" className="text-gray-500 hover:text-gray-300 text-xs sm:text-sm transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-gray-600 text-xs text-center sm:text-left">© 2026 TradeBotHub Pro. All rights reserved.</p>
          <p className="text-gray-600 text-xs">Trading involves risk. Past performance is not indicative of future results.</p>
        </div>
      </div>
    </footer>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalPlan, setModalPlan] = useState('pro')
  const prices = useLivePrices()

  // Auto-popup after 4s (once per session)
  useEffect(() => {
    const shown = sessionStorage.getItem('tbhEarlyShown')
    if (shown) return
    const t = setTimeout(() => {
      setModalOpen(true)
      sessionStorage.setItem('tbhEarlyShown', '1')
    }, 4000)
    return () => clearTimeout(t)
  }, [])

  // Handle checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') === 'success') {
      // Optionally show success toast here
    }
  }, [])

  const openModal = (plan = 'pro') => {
    setModalPlan(typeof plan === 'string' ? plan : 'pro')
    setModalOpen(true)
  }

  return (
    <div className="bg-[#050510] min-h-screen overflow-x-hidden">
      <Navbar onOpenModal={openModal} />
      <PriceTicker prices={prices} />
      <HeroSection onOpenModal={openModal} />
      <PlatformVideoSection />
      <LiveMarketsSection prices={prices} />
      <StatsSection />
      <FeaturesSection />
      <StrategySection />
      <BacktestingSection onOpenModal={openModal} />
      <AISection />
      <LivePerformanceSection />
      <ExchangesSection />
      <SignalMarketplaceSection onOpenModal={openModal} />
      <AcademySection onOpenModal={openModal} />
      <AudioSection />
      <BrochureSection />
      <PricingSection onOpenModal={openModal} />
      <SecuritySection />
      <CTASection onOpenModal={openModal} />
      <Footer />
      <EarlyAccessModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialPlan={modalPlan}
      />
    </div>
  )
}
